import React, { useState } from 'react';
import { registerAPICall } from '../../../Login/AuthService';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const RegisterForm = (): JSX.Element => {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validationSchema = yup.object({
    firstName: yup.string().required('First Name is required.'),
    lastName: yup.string().required('Last Name is required.'),
    email: yup
      .string()
      .trim()
      .email('Please enter a valid email address')
      .required('Email is required.'),
    password: yup
      .string()
      .required('Please specify your password')
      .min(8, 'The password must be at least 8 characters long.')
      .matches(
        /[A-Z]/,
        'The password must contain at least one uppercase letter.',
      )
      .matches(/\d/, 'The password must contain at least one number.'),
  });

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setErrorMessage(''); // Clear previous error message before submitting
    try {
      const response = await registerAPICall(values);
      if (response.status === 201) {
        setSuccessMessage(
          'Registration successful. Please check your email and click the link to validate your account.',
        );
      } else {
        setErrorMessage('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        (error as any).response?.data?.message ||
          'Something went wrong. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}
          gutterBottom
          color={'text.secondary'}
        >
          Register
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Create your account
        </Typography>
        <Typography color="text.secondary">
          Fill in the fields below to register.
        </Typography>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Enter your first name
            </Typography>
            <TextField
              label="First Name *"
              variant="outlined"
              name="firstName"
              fullWidth
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={
                formik.touched.firstName && formik.errors.firstName
                  ? String(formik.errors.firstName)
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Enter your last name
            </Typography>
            <TextField
              label="Last Name *"
              variant="outlined"
              name="lastName"
              fullWidth
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={
                formik.touched.lastName && formik.errors.lastName
                  ? String(formik.errors.lastName)
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Enter your email
            </Typography>
            <TextField
              label="Email *"
              variant="outlined"
              name="email"
              fullWidth
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email && formik.errors.email
                  ? String(formik.errors.email)
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }}>
              Enter your password
            </Typography>
            <TextField
              label="Password *"
              variant="outlined"
              name="password"
              type="password"
              fullWidth
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                formik.touched.password && formik.errors.password
                  ? String(formik.errors.password)
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {successMessage && (
            <Grid item xs={12}>
              <Typography color="success" variant="body2" gutterBottom>
                {successMessage}
              </Typography>
            </Grid>
          )}
          {errorMessage && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2" gutterBottom>
                {errorMessage}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              size="large"
              variant="contained"
              type="submit"
              fullWidth
              disabled={formik.isSubmitting}
            >
              Register
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography variant={'subtitle2'}>
              Already have an account?{' '}
              <Link component="a" href="/login" underline="none">
                Login here.
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default RegisterForm;
